import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  adminListCreditReconciliationRuns,
  adminRunCreditReconciliationNow,
} from "@/lib/api/credit-reconciliation.functions";

export const Route = createFileRoute(
  "/_authenticated/portal/admin/credit-reconciliation",
)({ component: Page });

function Page() {
  const list = useServerFn(adminListCreditReconciliationRuns);
  const run = useServerFn(adminRunCreditReconciliationNow);
  const qc = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["admin-credit-reconciliation"],
    queryFn: () => list(),
  });

  const mut = useMutation({
    mutationFn: () => run(),
    onSuccess: (r: any) => {
      toast.success(
        `Scanned ${r.scanned} paid orders — ${r.fixed} provisioned, ${r.failed} failed.`,
      );
      qc.invalidateQueries({ queryKey: ["admin-credit-reconciliation"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Reconciliation failed"),
  });

  const runs = q.data?.runs ?? [];

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-xl text-navy">
            Credit reconciliation
          </h2>
          <p className="text-xs text-muted-foreground">
            Runs daily at 03:15 SAST. Finds <strong>paid</strong> credit orders
            whose ledger entry is missing and provisions the credits
            automatically. The underlying <code>grant_credits</code> RPC is
            idempotent, so re-running is always safe.
          </p>
        </div>
        <Button
          onClick={() => mut.mutate(undefined as any)}
          disabled={mut.isPending}
          className="bg-navy hover:bg-navy/90"
        >
          {mut.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Run reconciliation now
        </Button>
      </div>

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : runs.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No runs recorded yet. Click <em>Run reconciliation now</em> to do a
          first sweep.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Started</th>
                <th className="px-3 py-2">Trigger</th>
                <th className="px-3 py-2 text-right">Scanned</th>
                <th className="px-3 py-2 text-right">Fixed</th>
                <th className="px-3 py-2 text-right">Failed</th>
                <th className="px-3 py-2">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {runs.map((r: any) => {
                const isOpen = open === r.id;
                const ok = r.failed_count === 0;
                return (
                  <>
                    <tr key={r.id} className={r.failed_count > 0 ? "bg-rose-50/40" : undefined}>
                      <td className="px-3 py-2 text-xs">
                        {new Date(r.started_at).toLocaleString("en-ZA")}
                      </td>
                      <td className="px-3 py-2 text-xs">{r.triggered_by}</td>
                      <td className="px-3 py-2 text-right">{r.scanned_count}</td>
                      <td className="px-3 py-2 text-right">
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          {r.fixed_count > 0 ? (
                            <CheckCircle2 className="h-3.5 w-3.5" />
                          ) : null}
                          {r.fixed_count}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span
                          className={`inline-flex items-center gap-1 ${
                            ok ? "text-muted-foreground" : "text-rose-700 font-semibold"
                          }`}
                        >
                          {!ok ? <AlertTriangle className="h-3.5 w-3.5" /> : null}
                          {r.failed_count}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => setOpen(isOpen ? null : r.id)}
                          className="text-xs font-medium text-navy underline-offset-2 hover:underline"
                        >
                          {isOpen ? "Hide" : "View"} ({(r.details ?? []).length})
                        </button>
                      </td>
                    </tr>
                    {isOpen ? (
                      <tr key={`${r.id}-detail`}>
                        <td colSpan={6} className="bg-muted/40 px-3 py-3">
                          {(r.details ?? []).length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                              All paid orders were already reconciled — nothing
                              to fix.
                            </p>
                          ) : (
                            <pre className="max-h-72 overflow-auto rounded bg-background p-3 text-[11px]">
                              {JSON.stringify(r.details, null, 2)}
                            </pre>
                          )}
                        </td>
                      </tr>
                    ) : null}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
